import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

export const listingApi = createApi({
    reducerPath: 'listingApi',
    baseQuery: (fetchBaseQuery({
        baseUrl: `${process.env.REACT_APP_BACKEND_HOST_OLD}/api`, prepareHeaders: (headers) => {
            const token = localStorage.getItem('prona_userToken');
            if (token) {
                headers.set("Authorization", `Bearer ${token}`);
            }
            return headers;
        },
    })),
    endpoints: (build) => ({
        getListings: build.query({
            query(params) {
                const queryParams = new URLSearchParams(params).toString()
                return {
                    url: `${process.env.REACT_APP_BACKEND_HOST_OLD}/api/admin/listings?${queryParams}`,
                    params: {},
                    method: 'GET',
                    headers: {}
                }
            },
            providesTags: ['Listings']
        }),
        getArchivedListings: build.query({
            query(params) {
                const queryParams = new URLSearchParams(params).toString()
                return {
                    url: `/admin/archive/listings?${queryParams}`,
                    params: {},
                    method: 'GET',
                    headers: {}
                }
            },
            providesTags: ['Archived Listings']
        }),
        getAdvancedSearchListings: build.query({
            query(params) {
                return {
                    url: `/listings/advancedSearch?${params}`,
                    params: {},
                    method: 'GET',
                    headers: {}
                }
            },
        }),
        getListingById: build.query({
            query(id) {
                return {
                    url: `${process.env.REACT_APP_BACKEND_HOST}/api/admin/listings/${id}`,
                    params: {},
                    method: 'GET',
                    headers: {}
                }
            },
            providesTags: ['Listing']
        }),
        getArchivedListingById: build.query({
            query(id) {
                return {
                    url: `/admin/archive/listings/${id}`,
                    params: {},
                    method: 'GET',
                    headers: {}
                }
            },
            // Answers with a `{ message }` object when the id is not archived.
            transformResponse: (response) =>
                response && response.listing_id ? response : null,
            providesTags: ['Archived Listing']
        }),
        revertListing: build.mutation({
            query(id) {
                return {
                    url: `/admin/listings/revertSoftDeletion/${id}`,
                    method: 'PATCH',
                }
            },
            invalidatesTags: ['Listings', 'Archived Listings', 'Archived Listing']
        }),
        updateListing: build.mutation({
            query({ id, data }) {
                return {
                    url: `${process.env.REACT_APP_BACKEND_HOST_OLD}/api/admin/listings/edit/${id}`,
                    method: 'PUT',
                    body: data
                }
            },
            invalidatesTags: ['Listings', 'Listing']
        }),
        softDeleteById: build.mutation({
            query(id) {
                return {
                    url: `/admin/listings/softDelete/${id}`,
                    method: 'PATCH',
                }
            },
            async onQueryStarted(id, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                } catch (error) {
                    return error
                }
            },
            invalidatesTags: ['Listings', 'Archived Listings']
        }),
        deleteManyListings: build.mutation({
            query(ids) {
                return {
                    url: '/admin/listings/softDeleteMultiple',
                    method: 'PATCH',
                    body: { listingsToDelete: ids }
                }
            },
            invalidatesTags: ['Listings']
        }),
        changeListingPauseStatus: build.mutation({
            query({ id, data }) {
                return {
                    url: `/admin/listings/changePause/${id}`,
                    method: 'PATCH',
                    body: data
                }
            },
            async onQueryStarted(id, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                } catch (error) {
                    return error
                }
            },
            invalidatesTags: ['Listings']
        }),
        changeListingStatus: build.mutation({
            query({ id, data }) {
                return {
                    url: `/admin/listings/changeStatus/${id}`,
                    method: 'PATCH',
                    body: data
                }
            },
            invalidatesTags: ['Listings']
        }),
        renewListing: build.mutation({
            query(id) {
                return {
                    url: `/admin/listings/refresh/${id}`,
                    method: 'POST',
                }
            },
            async onQueryStarted(id, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                } catch (error) {
                    return error
                }
            },
            invalidatesTags: ['Listings']
        }),
        // The TOP dialog's details: source, end date, who set it, and whether
        // it can be changed here (plan-slot / paid TOPs cannot).
        getListingPremium: build.query({
            query: (id) => `/admin/listings/${id}/premium`,
            keepUnusedDataFor: 0,
            providesTags: ['Listings']
        }),
        updateListingPremium: build.mutation({
            query({ id, data }) {
                return {
                    url: `/admin/listings/${id}/premium`,
                    method: 'PATCH',
                    body: data
                }
            },
            invalidatesTags: ['Listings']
        }),
        // Zero-filled listing counts per time bucket, split by status, for the
        // listings dashboard chart. Answers a bare { period, buckets: [...] } —
        // no { success, data } envelope — so read response.buckets directly.
        getListingTrend: build.query({
            query(period = 'month') {
                return {
                    url: `/admin/listings/stats/listingTrend?period=${encodeURIComponent(period)}`,
                    method: 'GET'
                }
            },
            providesTags: ['Listings']
        }),
        // Promotion analytics for the Top Listings dashboard — one call backs all
        // three cards. Answers a bare { period, promotionTrend, promotionSource,
        // promotionExpiry } — no { success, data } envelope — so read the three
        // sections off the response directly.
        //
        // Only promotionTrend varies with ?period=; promotionSource and
        // promotionExpiry are point-in-time snapshots of what is promoted right
        // now and come back identical for every period value.
        //
        // Defaults to 'year' rather than the 'month' getListingTrend uses:
        // promotions are low-volume, so a month is routinely an all-zero axis.
        // Pending edit = a seller's staged edit of a LIVE listing. Field-level
        // current → proposed diff, then apply or discard without touching the
        // live row's status (backend: adminModel.getPendingEditDiff).
        getPendingEdit: build.query({
            query: (id) => `/admin/listings/${id}/pendingEdit`,
            keepUnusedDataFor: 0,
            providesTags: ['Listing', 'Listings']
        }),
        approvePendingEdit: build.mutation({
            query: (id) => ({ url: `/admin/listings/${id}/approvePendingEdit`, method: 'PATCH' }),
            invalidatesTags: ['Listings', 'Listing']
        }),
        rejectPendingEdit: build.mutation({
            query: (id) => ({ url: `/admin/listings/${id}/rejectPendingEdit`, method: 'PATCH' }),
            invalidatesTags: ['Listings', 'Listing']
        }),
        getTopListingStats: build.query({
            query(period = 'year') {
                return {
                    url: `/admin/listings/stats/topListingStats?period=${encodeURIComponent(period)}`,
                    method: 'GET'
                }
            },
            providesTags: ['Listings']
        }),
    }),
});

export const {
    useGetListingsQuery,
    useGetArchivedListingsQuery,
    useLazyGetListingsQuery,
    useGetListingByIdQuery,
    useGetArchivedListingByIdQuery,
    useRevertListingMutation,
    useGetAdvancedSearchListingsQuery,
    useSoftDeleteByIdMutation,
    useChangeListingPauseStatusMutation,
    useChangeListingStatusMutation,
    useUpdateListingMutation,
    useDeleteManyListingsMutation,
    useRenewListingMutation,
    useGetListingTrendQuery,
    useGetTopListingStatsQuery,
    useUpdateListingPremiumMutation,
    useGetListingPremiumQuery,
    useGetPendingEditQuery,
    useApprovePendingEditMutation,
    useRejectPendingEditMutation } = listingApi;
